import { t as msg, countText, themeName } from "../lib/i18n";
import { Challenge } from "../components/Challenge";
import { useEffect, useMemo, useRef, useState } from "react";
import {
  ArrowRight,
  Check,
  ChevronLeft,
  Copy,
  ExternalLink,
} from "lucide-react";
import type {
  Draft,
  Template,
  Theme,
  User,
  Card,
  Friend,
} from "../../../../packages/shared/src/types";
import { CardPreview } from "../components/CardPreview";
import { api, ApiError, secret } from "../lib/api";
import { draftStore } from "../lib/drafts";
import { canContinueSend, draftContents, restoreSendIntent, type SendIntent } from "../lib/sendIntent";
import { ConfirmDialog } from "../components/ConfirmDialog";
const intentStorageKey = "promise-card:send-intent";
export function Editor({
  templates,
  user,
  signIn,
  accountReady,
  signInOpen,
}: {
  templates: Template[];
  user: User | null;
  signIn: () => void;
  accountReady: boolean;
  signInOpen: boolean;
}) {
  const drafts = useMemo(() => draftStore(user?.id), [user?.id]);
  const [leaveTo, setLeaveTo] = useState<string | null>(null);
  const savedContents = useRef<string | null>(null);
  const persistedOwner = useRef<string | null>(user?.id || null);
  const [draft, setDraft] = useState<Draft | null>(null),
    [view, setView] = useState<"edit" | "preview">("edit"),
    [saved, setSaved] = useState(false),
    [error, setError] = useState(""),
    [busy, setBusy] = useState(false),
    [sent, setSent] = useState<{
      card: Card;
      shareUrl: string;
      ogImageUrl: string;
    } | null>(null),
    [copied, setCopied] = useState(false);
  const initialized = useRef(false);
  const [sendIntent, setSendIntent] = useState<SendIntent | null>(null);
  const activeIntent = useRef<SendIntent | null>(null);
  const currentDraft = useRef(draft);
  currentDraft.current = draft;
  const sending = useRef(false);
  const loginWasOpen = useRef(false);
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; activeIntent.current = null; };
  }, []);
  function hasActiveIntent() { return activeIntent.current !== null; }
  function activateIntent(intent: SendIntent) {
    activeIntent.current = intent;
    setSendIntent(intent);
  }
  function cancelSend() {
    activeIntent.current = null;
    setSendIntent(null);
    loginWasOpen.current = false;
    try { sessionStorage.removeItem(intentStorageKey); } catch {}
    const url = new URL(location.href);
    url.searchParams.delete("send");
    history.replaceState(null, "", url.pathname + url.search);
    setDraft(previous => previous?.pendingSend ? { ...previous, pendingSend: false } : previous);
  }
  useEffect(() => {
    if (!sendIntent || sending.current) return;
    if (signInOpen) loginWasOpen.current = true;
    if (user && draft && canContinueSend(sendIntent, draft, user.id)) {
      if (!sendIntent.accountId) {
        void drafts.claimGuest(sendIntent).then(() => {
          if (activeIntent.current?.id === sendIntent.id && currentUserId.current === user.id) {
            persistedOwner.current = user.id;
            activateIntent({ ...sendIntent, accountId: user.id });
          }
        }).catch(() => { cancelSend(); setError(msg("Your draft could not be saved on this device.")); });
      }
    } else if (sendIntent.accountId || (loginWasOpen.current && !signInOpen && accountReady)) {
      cancelSend();
    }
  }, [user?.id, signInOpen, accountReady, sendIntent, draft]);
  const currentUserId = useRef(user?.id);
  currentUserId.current = user?.id;
  const [friends, setFriends] = useState<Friend[]>([]);
  const [friendsState, setFriendsState] = useState<
    "loading" | "ready" | "error"
  >("loading");
  const [friendsRetry, setFriendsRetry] = useState(0);
  useEffect(() => {
    let live = true;
    setFriends([]);
    setFriendsState("loading");
    if (user)
      api<{ friends: Friend[] }>("/api/friends")
        .then((r) => {
          if (live) {
            setFriends(r.friends);
            setFriendsState("ready");
          }
        })
        .catch(() => {
          if (live) setFriendsState("error");
        });
    return () => {
      live = false;
    };
  }, [user?.id, friendsRetry]);
  useEffect(() => {
    let live = true;
    if (!accountReady || initialized.current) return;
    (async () => {
      let handoff: SendIntent | null = null;
      try { const raw = sessionStorage.getItem(intentStorageKey); handoff = raw ? JSON.parse(raw) as SendIntent : null; } catch {}
      const query = new URLSearchParams(location.search),
        existing = query.get("draft"),
        owned = existing ? await drafts.get(existing) : null,
        stored = owned || (existing && handoff?.draftId === existing && handoff.id === query.get("send") ? await drafts.claimGuest(handoff) : null),
        t = templates.find(
          (t) => t.id === (query.get("template") || "blank-card"),
        );
      if (existing && !stored) { initialized.current = true; setError(msg("This draft is unavailable for this account.")); return; }
      if (!t && !stored) return;
      const next = stored || {
        id: crypto.randomUUID(),
        title: t!.id === "blank-card" ? "" : t!.defaultTitle,
        promise: t!.id === "blank-card" ? "" : t!.defaultPromise,
        signature: user?.defaultSignature || "",
        recipientName: "",
        note: "",
        usesTotal: Math.min(5, t!.defaultUses),
        templateId: t!.id,
        theme: t!.theme,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        delivery: query.get("friend")
          ? { mode: "direct" as const, recipientUserId: query.get("friend")! }
          : { mode: "link" as const },
        updatedAt: new Date().toISOString(),
      };
      if (live) {
        initialized.current=true;
        persistedOwner.current = user?.id || null;
        savedContents.current = stored ? draftContents(stored) : null;
        setSaved(!!stored);
        let restored: SendIntent | null = null;
        try { restored = restoreSendIntent(sessionStorage.getItem(intentStorageKey), query.get("send"), next, user?.id); } catch {}
        setDraft({ ...next, pendingSend: !!restored });
        if (restored) activateIntent(restored);
        else { try { sessionStorage.removeItem(intentStorageKey); } catch {} }
        history.replaceState(null, "", `/create?draft=${next.id}`);
      }
    })().catch((e) =>
      setError(
        msg("Your browser couldn’t save this draft. Check storage permissions."),
      ),
    );
    return () => {
      live = false;
    };
  }, [templates, accountReady, drafts]);
  const dirty = !!draft && !sent && !draft.sentCardId && !draft.clientRequestId &&
    (Boolean(draft.title || draft.promise || draft.note || draft.signature)) && draftContents(draft) !== savedContents.current;
  useEffect(() => {
    const beforeUnload = (event: BeforeUnloadEvent) => { if (dirty && currentDraft.current && draftContents(currentDraft.current) !== savedContents.current) { event.preventDefault(); event.returnValue = ""; } };
    const leave = (event: MouseEvent) => {
      const anchor = (event.target as Element).closest?.("a[href]") as HTMLAnchorElement | null;
      if (!anchor || anchor.target === "_blank" || event.metaKey || event.ctrlKey || event.defaultPrevented) return;
      if (dirty) { event.preventDefault(); setLeaveTo(anchor.href); }
      else if (activeIntent.current) cancelSend();
    };
    addEventListener("beforeunload", beforeUnload); document.addEventListener("click", leave);
    return () => { removeEventListener("beforeunload", beforeUnload); document.removeEventListener("click", leave); };
  }, [dirty]);
  async function saveDraft(destination?: string) {
    if (!draft || busy) return;
    setBusy(true);
    try {
      await drafts.put(draft); savedContents.current = draftContents(draft); setSaved(true);
      if (destination) { setLeaveTo(null); location.assign(destination); }
    } catch { setError(msg("Your draft could not be saved on this device.")); }
    finally { setBusy(false); }
  }
  function change<K extends keyof Draft>(key: K, value: Draft[K]) {
    cancelSend();
    setSaved(false);
    if (draft)
      setDraft({ ...draft, pendingSend: false, [key]: value, updatedAt: new Date().toISOString() });
  }
  async function requestSend(e: React.FormEvent) {
    e.preventDefault();
    if (!draft || sending.current || hasActiveIntent()) return;
    if (draft.senderUserId && user && draft.senderUserId !== user.id) {
      setError(msg("This send request belongs to another account. Sign in to that account to retry."));
      return;
    }
    setError("");
    const intent: SendIntent = {
      id: crypto.randomUUID(), draftId: draft.id, contents: draftContents(draft),
      accountId: user?.id, createdAt: Date.now(),
    };
    activateIntent(intent);
    if (!user) {
      try {
        // Same-tab OAuth returns and email approval may continue only this exact
        // draft. Opening any other draft, tab or URL does not authorize sending.
        await drafts.put({ ...draft, pendingSend: true });
        savedContents.current = draftContents(draft);
        setSaved(true);
        if (activeIntent.current?.id !== intent.id || !mounted.current) return;
        sessionStorage.setItem(intentStorageKey, JSON.stringify(intent));
        history.replaceState(null, "", `/create?draft=${draft.id}&send=${intent.id}`);
        signIn();
      } catch {
        cancelSend();
        setError(msg("Your draft could not be saved on this device."));
      }
    }
  }
  async function submitSend(intent: SendIntent, token: string) {
    const snapshot = currentDraft.current;
    const accountId = currentUserId.current;
    if (!token || !snapshot || !accountId || !mounted.current || sending.current ||
        activeIntent.current?.id !== intent.id || intent.accountId !== accountId ||
        !canContinueSend(intent, snapshot, accountId)) return;
    sending.current = true;
    setBusy(true);
    setError("");
    const pending: Draft = {
      ...snapshot,
      clientRequestId: snapshot.clientRequestId || crypto.randomUUID(),
      shareToken: snapshot.delivery?.mode === "direct" ? undefined : snapshot.shareToken || secret(),
      senderUserId: accountId,
      delivery: snapshot.delivery || { mode: "link" },
      pendingSend: true,
    };
    try {
      // Save recovery proof before the request; never replace it on an uncertain result.
      await drafts.put(pending);
      if (!mounted.current || activeIntent.current?.id !== intent.id || currentUserId.current !== accountId) return;
      setDraft(pending);
      const { id, updatedAt, sentCardId, pendingSend, senderUserId, ...payload } = pending;
      const result = await api<{ card: Card; shareUrl: string; ogImageUrl: string }>("/api/cards", "POST", {
        ...payload, expectedSenderUserId: accountId, clientDraftId: id, turnstileToken: token,
      });
      const completed = { ...pending, pendingSend: false, sentCardId: result.card.id };
      // A confirmed server result is final even if the device runs out of storage.
      await drafts.put(completed).catch(() => {
        if (mounted.current) setError(msg("Sent successfully. This device could not save the local copy. Find your card in My cards."));
      });
      if (!mounted.current) return;
      savedContents.current = draftContents(completed);
      setDraft(completed);
      if (currentUserId.current !== accountId) {
        setError(msg("Your account changed. Return to the original account to view this sent card."));
        return;
      }
      setSent(result);
    } catch (e) {
      const failed = {
        ...pending, pendingSend: false,
        ...(e instanceof ApiError && [403, 422, 429].includes(e.status)
          ? { clientRequestId: undefined, shareToken: undefined, senderUserId: undefined } : {}),
      };
      await drafts.put(failed).catch(() => {});
      if (mounted.current) {
        setError((e as Error).message);
        setDraft(failed);
        if (e instanceof ApiError && e.status === 401) signIn();
      }
    } finally {
      sending.current = false;
      if (mounted.current) { setBusy(false); cancelSend(); }
    }
  }
  async function copy() {
    if (!sent) return;
    try {
      await navigator.clipboard.writeText(sent.shareUrl);
      setCopied(true);
    } catch {
      setError(msg("Select and copy the link below."));
    }
  }
  if (draft && persistedOwner.current && persistedOwner.current !== user?.id) return <section className="wrap section"><p>{msg("This draft is unavailable for this account.")}</p><a href="/create">{msg("Back to cards")}</a></section>;
  if (!draft)
    return (
      <div className="wrap section">
        <p>{error || msg("Getting your stationery ready…")}</p>
      </div>
    );
  if (sent)
    return (
      <section className="wrap sent-page">
        <div>
          <div className="round-icon">
            <Check />
          </div>
          <p className="eyebrow">{msg("READY FOR A LITTLE JOY")}</p>
          <h1 className="page-title">{msg("Your promise is ready.")}</h1>
          <p className="intro">
            {sent.card.deliveryMode === "direct"
              ? sent.card.deliveredAt
                ? msg("Your card is already in your friend’s received cards. You can also share a reminder in any chat.")
                : msg("Your card is being checked before delivery. No reminder can be shared until it reaches your friend.")
              : msg("Send this link in any chat. The first person to accept it will keep it in their received cards.")}
          </p>
          {sent.card.moderation === "pending" && (
            <p className="notice">
              {msg("Your card is being checked. It will open once the check is complete.")}</p>
          )}
          {(sent.card.deliveryMode !== "direct" || sent.card.reminderUrl) && (
            <label>
              {sent.card.deliveryMode === "direct"
                ? msg("Reminder link")
                : msg("Share link")}
              <div className="copy-row">
                <input
                  readOnly
                  value={sent.shareUrl}
                  onFocus={(e) => e.target.select()}
                />
                <button className="button" onClick={copy}>
                  <Copy size={17} />
                  {copied ? msg("Copied") : msg("Copy")}
                </button>
              </div>
            </label>
          )}
          {sent.card.deliveryMode === "direct" && !sent.card.deliveredAt && (
            <button
              className="button secondary"
              onClick={async () => {
                try {
                  const result = await api<{ card: Card }>(`/api/cards/${sent.card.id}`);
                  setSent({ ...sent, card: result.card });
                } catch (e) {
                  setError((e as Error).message);
                }
              }}
            >
              {msg("Check delivery")}</button>
          )}
          {error && (
            <p role="alert" className="error">
              {error}
            </p>
          )}
          {!!navigator.share &&
            (sent.card.deliveryMode !== "direct" ||
              !!sent.card.reminderUrl) && (
              <button
                className="button secondary"
                onClick={() =>
                  navigator
                    .share({
                      title:
                        sent.card.deliveryMode !== "direct" &&
                        sent.card.moderation === "ok"
                          ? msg("{p0} sent you a promise card", {p0: draft.signature})
                          : "A Promise Card",
                      url: sent.shareUrl,
                      ...(sent.card.deliveryMode === "direct"
                        ? {
                            text: msg("I sent you a promise card. Sign in to A Promise Card to see it."),
                          }
                        : {}),
                    })
                    .catch(() => {})
                }
              >
                {sent.card.deliveryMode === "direct"
                  ? msg("Share a reminder")
                  : msg("Share your card")}{" "}
                <ArrowRight size={17} />
              </button>
            )}
          <div className="inline-links">
            <a href="/cards?tab=sent">{msg("Go to my cards")}</a>
            <a href={"/cards/" + sent.card.id}>
              {msg("Open card")}<ExternalLink size={14} />
            </a>
          </div>
        </div>
        <CardPreview card={draft} details />
      </section>
    );
  if (draft.sentCardId)
    return (
      <section className="wrap section narrow">
        <h1 className="page-title">{msg("This promise is on its way.")}</h1>
        <p>
          {msg("You already sent this draft. Find it in My cards, or copy the original link.")}</p>
        <a
          className="button"
          href={
            draft.delivery?.mode === "direct"
              ? `/cards/${draft.sentCardId}`
              : `/c/${draft.sentCardId}?k=${draft.shareToken}`
          }
        >
          {msg("Open your card")}</a>
        <a className="text-link" href="/cards?tab=sent">
          {msg("Go to my cards")}</a>
      </section>
    );
  const frozen = !!draft.clientRequestId;
  return (
    <section className="wrap editor" data-view={view}>
      {leaveTo && <ConfirmDialog title={msg("Save this draft?")} busy={busy} destructive={false} confirmLabel={msg("Save draft")} cancelLabel={msg("Keep editing")} onConfirm={() => void saveDraft(leaveTo)} onCancel={() => setLeaveTo(null)}>
        {msg("Keep your idea on this device, or leave without saving.")}
        <button type="button" className="text-button danger-text" onClick={() => { savedContents.current = draftContents(draft); setSaved(true); setLeaveTo(null); cancelSend(); location.assign(leaveTo); }}>{msg("Discard changes")}</button>
      </ConfirmDialog>}
      <div className="editor-heading">
        <a className="text-link" href={draft.delivery?.mode === "direct" ? `/create?friend=${draft.delivery.recipientUserId}` : "/create"}>
          <ChevronLeft size={16} /> {msg("Back to cards")}</a>
        <button type="button" className="text-button" disabled={busy || frozen || saved} onClick={() => void saveDraft()}>
          {saved ? msg("Saved on this device") : msg("Save draft")}
        </button>
      </div>
      <div className="editor-views" aria-label={msg("Editor view")}>
        <button
          type="button"
          aria-pressed={view === "edit"}
          aria-controls={"editor-form"}
          onClick={() => setView("edit")}
        >
          {msg("Edit your card")}</button>
        <button
          type="button"
          aria-pressed={view === "preview"}
          aria-controls={"editor-preview"}
          onClick={() => setView("preview")}
        >
          {msg("Preview")}</button>
      </div>
      <form id="editor-form" className="editor-form" onSubmit={requestSend}>
        <h1 className="page-title">
          {msg("A little promise.")}<br />
          {msg("All your own.")}</h1>
        <fieldset disabled={busy || frozen}>
          <label>
            {msg("Delivery")}<select
              value={
                draft.delivery?.mode === "direct"
                  ? draft.delivery.recipientUserId
                  : "link"
              }
              onChange={(e) =>
                change(
                  "delivery",
                  e.target.value === "link"
                    ? { mode: "link" }
                    : { mode: "direct", recipientUserId: e.target.value },
                )
              }
            >
              <option value="link">{msg("Share a link")}</option>
              {friends.map((f) => (
                <option key={f.id} value={f.id}>
                  {msg("Send to {name}",{name:f.note || f.displayName})}
                </option>
              ))}
              {draft.delivery?.mode === "direct" &&
                !friends.some(
                  (f) =>
                    f.id ===
                    (draft.delivery as { recipientUserId: string })
                      .recipientUserId,
                ) && (
                  <option value={draft.delivery.recipientUserId}>
                    {!accountReady || (user && friendsState === "loading")
                      ? msg("Checking recipient…")
                      : !user
                        ? msg("Sign in to confirm recipient")
                        : friendsState === "error"
                          ? msg("Couldn’t load friends")
                          : msg("Friend unavailable — choose a recipient")}
                  </option>
                )}
            </select>
            {friendsState === "error" && (
              <button
                className="text-button"
                type="button"
                onClick={() => setFriendsRetry((value) => value + 1)}
              >
                {msg("Retry loading friends")}</button>
            )}
            <small>
              {draft.delivery?.mode === "direct"
                ? msg("Delivered to this friend’s account. Chat sharing is an optional reminder afterward.")
                : msg("Anyone with the link can be the first to accept. For existing friends, choose direct delivery.")}
            </small>
          </label>
          <label>
            {msg("Card title")}<span>{draft.title.length}/40</span>
            <input
              value={draft.title}
              maxLength={40}
              required
              placeholder={msg("Coffee’s on me")}
              onChange={(e) => change("title", e.target.value)}
            />
          </label>
          <label>
            {msg("Your promise")}<span>{draft.promise.length}/200</span>
            <textarea
              value={draft.promise}
              rows={3}
              maxLength={200}
              required
              placeholder={msg("One coffee, any size, my treat.")}
              onChange={(e) => change("promise", e.target.value)}
            />
          </label>
          <div className="form-row">
            <label>
              {msg("To")}<small>{msg("optional")}</small>
              <input
                value={draft.recipientName}
                maxLength={30}
                placeholder={msg("Your person")}
                onChange={(e) => change("recipientName", e.target.value)}
              />
              <small>
                {msg("Add a name or nickname to tell your sent cards apart.")}</small>
            </label>
            <label>
              {msg("From")}<input
                value={draft.signature}
                maxLength={30}
                required
                placeholder={msg("Your name")}
                onChange={(e) => change("signature", e.target.value)}
              />
            </label>
          </div>
          <label>
            {msg("A private little note")}<small>{msg("optional")}</small>
            <textarea
              value={draft.note}
              maxLength={300}
              rows={2}
              placeholder={msg("Just because you make my days better.")}
              onChange={(e) => change("note", e.target.value)}
            />
          </label>
          <div className="form-row">
            <label>
              {msg("Number of uses")}<select
                value={draft.usesTotal}
                onChange={(e) => change("usesTotal", Number(e.target.value))}
              >
                {[1, 2, 3, 4, 5].map((n) => (
                  <option key={n} value={n}>
                    {countText("usesCount",n)}
                  </option>
                ))}
              </select>
            </label>
            <div className="theme-picker">
              <span>{msg("Card color")}</span>
              <div>
                {(["sage", "peach", "lavender", "butter"] as Theme[]).map(
                  (t) => (
                    <button
                      key={t}
                      type="button"
                      className={`swatch theme-${t}`}
                      aria-label={themeName(t)}
                      aria-pressed={draft.theme === t}
                      onClick={() => change("theme", t)}
                    >
                      {draft.theme === t && <Check size={18} />}
                    </button>
                  ),
                )}
              </div>
            </div>
          </div>
          <details>
            <summary>{msg("A date to open. A date to use.")}</summary>
            <div className="form-row">
              <label>
                {msg("Opens on")}<small>{msg("optional")}</small>
                <input
                  type="datetime-local"
                  onChange={(e) =>
                    change(
                      "unlockAt",
                      e.target.value
                        ? new Date(e.target.value).toISOString()
                        : undefined,
                    )
                  }
                  defaultValue={localDate(draft.unlockAt)}
                />
              </label>
              <label>
                {msg("Use before")}<small>{msg("optional")}</small>
                <input
                  type="datetime-local"
                  min={localDate(new Date().toISOString())}
                  onChange={(e) =>
                    change(
                      "expiresAt",
                      e.target.value
                        ? new Date(e.target.value).toISOString()
                        : undefined,
                    )
                  }
                  defaultValue={localDate(draft.expiresAt)}
                />
              </label>
            </div>
            <small>{msg("Dates use your time zone:")}{draft.timezone}.</small>
          </details>
        </fieldset>
        {(!draft.title.trim() ||
          !draft.promise.trim() ||
          !draft.signature.trim()) && (
          <p id="send-guidance" className="field-guidance">
            {msg("Add a title, a promise and your name before sending.")}</p>
        )}
        {frozen && (
          <p className="notice">
            {msg("Your send request is saved. Retry below to safely recover the same card.")}</p>
        )}
        {error && (
          <p className="error" role="alert">
            {error}
          </p>
        )}
        {sendIntent?.accountId && sendIntent.accountId === user?.id && (
          <div aria-busy={busy}>
            <Challenge key={sendIntent.id} action="send" onToken={token => void submitSend(sendIntent, token)} />
          </div>
        )}
        {sendIntent && !busy && (
          <button type="button" className="text-button full" onClick={cancelSend}>
            {msg("Cancel sending")}
          </button>
        )}
        <button
          className="button full"
          aria-describedby={
            !draft.title.trim() ||
            !draft.promise.trim() ||
            !draft.signature.trim()
              ? "send-guidance"
              : undefined
          }
          disabled={busy || !!sendIntent}
        >
          {busy
            ? msg("Making your promise…")
            : sendIntent
              ? msg("Verifying…")
              : frozen
              ? msg("Retry sending this card")
              : msg("Ready to send")}
          <ArrowRight size={18} />
        </button>
        <p className="form-footnote">
          {user
            ? draft.delivery?.mode === "direct"
              ? msg("Your friend will see this card when they next open My cards.")
              : msg("Your card will be ready to share as a link.")
            : msg("You’ll sign in when you’re ready to send.")}
        </p>
      </form>
      <aside
        id="editor-preview"
        className="preview-pane"
        aria-label={msg("Card preview")}
      >
        <p className="eyebrow">{msg("A LITTLE PREVIEW")}</p>
        <CardPreview card={draft} details />
        <p className="preview-caption">
          {msg("A card worth keeping. A promise worth making.")}</p>
        <div className="privacy-note">
          {draft.delivery?.mode === "direct"
            ? msg("Chat previews show only our brand. Your promise and both accounts stay private.")
            : msg("Your private note stays inside the card. Chat previews show only the title and your signature.")}
        </div>
        <button
          type="button"
          className="button secondary full preview-return"
          onClick={() => setView("edit")}
        >
          {msg("Continue editing")}</button>
      </aside>
    </section>
  );
}
function localDate(iso?: string) {
  if (!iso) return "";
  const d = new Date(iso);
  return new Date(d.getTime() - d.getTimezoneOffset() * 60000)
    .toISOString()
    .slice(0, 16);
}
